import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './PaperSubmissionStatus.css';
import Navbar from './Navbar';
import Footer from './Footer';

const PaperSubmissionStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paperCode = searchParams.get('code');
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [paper, setPaper] = useState(null);
  const [updatedFile, setUpdatedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const fileInputRef = useRef(null);

  // Derived flag: has user already uploaded an updated manuscript?
  const hasUpdatedManuscript = Boolean(paper?.updatedPaperFile || paper?.updatedManuscriptUrl);

  // Recommendation from admin (if available)
  const recommendationRaw = (paper?.review?.recommendation || paper?.recommendation || '').trim();
  const recommendation = recommendationRaw.toLowerCase();

  // Derive whether user is allowed to upload a revision based on recommendation
  const canUploadRevision = (() => {
    if (!recommendation) {
      // Fallback to old behavior: allow only when reviewer comments exist
      return Boolean(paper?.review?.reviewerComments && paper.review.reviewerComments.trim()) && !hasUpdatedManuscript;
    }
    // For frontend-only mapping, allow upload for Minor, Major, and Reject; not for Accept
    if (recommendation === 'accept') return false;
    if (
      recommendation.includes('minor') ||
      recommendation.includes('major') ||
      recommendation === 'reject' || recommendation === 'rejected'
    ) {
      return !hasUpdatedManuscript;
    }
    return false;
  })();

  const getDisplayStatus = () => {
    if (!recommendation) return paper.status;
    // Map legacy to new display labels (frontend-only)
    if (recommendation === 'accept' || recommendation === 'accepted') return 'Accept without change';
    if (recommendation.includes('minor')) return 'Accept with suggested but not mandatory changes';
    if (recommendation.includes('major')) return 'Accepted subject to mandatory changes';
    if (recommendation === 'reject' || recommendation === 'rejected') return 'Wish to see revised version';
    return paper.status;
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const uid = sessionStorage.getItem('uid');
    
    if (token && uid) {
      setIsAuthenticated(true);
      fetchPaperDetails();
    } else {
      setErrorMessage('Please log in to view paper status.');
      setTimeout(() => {
        navigate('/login-signup?redirect=/paper-submission-status?code=' + paperCode);
      }, 2000);
    }
  }, [paperCode, navigate]);

  const fetchPaperDetails = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`https://api.rescons.in/api/papers/${paperCode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPaper(response.data.paper);
    } catch (error) {
      console.error('Error fetching paper details:', error);
      if (error.response?.status === 404) {
        setErrorMessage('Paper not found. Please check the paper code.');
      } else if (error.response?.status === 401) {
        setErrorMessage('Authentication failed. Please log in again.');
      } else {
        setErrorMessage('Failed to load paper details. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    // Prevent selecting new file if already frozen
    if (paper && (paper.updatedPaperFile || paper.updatedManuscriptUrl)) {
      e.target.value = '';
      return;
    }
    const file = e.target.files[0];
    if (file) {
      // Validate file type - only .doc and .docx allowed
      const allowedTypes = ['.doc', '.docx'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        setFileError('Please select a .doc or .docx file only. PDF files are not allowed.');
        setUpdatedFile(null);
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setFileError('File size must be less than 10MB');
        setUpdatedFile(null);
        return;
      }

      setFileError('');
      setUpdatedFile(file);
    }
  };

  const handleUploadUpdatedManuscript = async () => {
    if (!updatedFile) {
      setFileError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setFileError('');
    setUploadSuccess('');

    try {
      const token = sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('updatedPaperFile', updatedFile);

             const response = await axios.put(
         `https://api.rescons.in/api/papers/${paperCode}/update`,
         formData,
         {
           headers: {
             'Authorization': `Bearer ${token}`,
             'Content-Type': 'multipart/form-data'
           }
         }
       );

      setUploadSuccess('Revised manuscript uploaded successfully!');
      setUpdatedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh paper details to get updated information
      fetchPaperDetails();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error uploading revised manuscript:', error);
      setFileError(error.response?.data?.error || 'Failed to upload revised manuscript');
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = (url, filename) => {
    // For PDFs, try to force download by adding query parameters
    const isPDF = filename.toLowerCase().endsWith('.pdf');
    const downloadUrl = isPDF ? `${url}?fl_attachment` : url;
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // For PDFs, try to open in new tab first, then download
    if (isPDF) {
      link.click();
      // Also try direct download after a short delay
      setTimeout(() => {
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 1000);
    } else {
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'status-badge pending';
      case 'accepted':
        return 'status-badge accepted';
      case 'accepted with minor revision':
        return 'status-badge accepted-minor';
      case 'major revisions required':
        return 'status-badge revision-required';
      case 'revision required':
        return 'status-badge revision-required';
      case 'revision submitted':
        return 'status-badge revision-submitted';
      case 'rejected':
        return 'status-badge rejected';
      default:
        return 'status-badge pending';
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Navbar />
        <div className="paper-status">
          <div className="paper-status-wrapper">
            <div className="loading-message">
              <p>{errorMessage || 'Loading...'}</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="paper-status">
          <div className="paper-status-wrapper">
            <div className="loading-message">
              <p>Loading paper details...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (errorMessage) {
    return (
      <>
        <Navbar />
        <div className="paper-status">
          <div className="paper-status-wrapper">
            <div className="error-message">
              {errorMessage}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!paper) {
    return (
      <>
        <Navbar />
        <div className="paper-status">
          <div className="paper-status-wrapper">
            <div className="error-message">
              Paper not found. Please check the paper code.
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="paper-status">
        <div className="paper-status-wrapper">
          {/* Header */}
          <header className="paper-status-header">
            <h1>Paper Submission Status</h1>
            <p className="paper-status-description">
              Track the status of your paper submission and manage updates.
            </p>
          </header>

          {/* Success Messages */}
          {uploadSuccess && (
            <div className="success-message">
              {uploadSuccess}
            </div>
          )}

          {/* Three Column Layout */}
          <div className="paper-status-content">
            {/* Column 1: Paper Details */}
            <div className="status-column">
              <h3>Paper Information</h3>
              <div className="paper-info-grid">
                <div className="info-item">
                  <label>Paper Code:</label>
                  <span>{paper.paperCode}</span>
                </div>
                <div className="info-item">
                  <label>Title:</label>
                  <span>{paper.title}</span>
                </div>
                <div className="info-item">
                  <label>Paper Type:</label>
                  <span>{paper.paperType}</span>
                </div>
                <div className="info-item">
                  <label>Status:</label>
                  <span className={getStatusBadgeClass(getDisplayStatus())}>
                    {getDisplayStatus()}
                  </span>
                </div>
                <div className="info-item">
                  <label>Submitted:</label>
                  <span>{new Date(paper.submittedAt).toLocaleDateString()}</span>
                </div>
                {paper.updatedAt && paper.updatedAt !== paper.submittedAt && (
                  <div className="info-item">
                    <label>Last Updated:</label>
                    <span>{new Date(paper.updatedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Download & Comments */}
            <div className="status-column">
              <h3>Download Papers</h3>
              <div className="download-buttons">
                {paper.paperFile && (
                  <button 
                    className="download-btn original"
                    onClick={() => downloadFile(paper.paperFile, `Original_${paper.paperCode}.pdf`)}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                    Download Original Paper
                  </button>
                )}
                
                {paper.updatedPaperFile && (
                  <button 
                    className="download-btn updated"
                    onClick={() => downloadFile(paper.updatedPaperFile, `Updated_${paper.paperCode}.pdf`)}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                    Download Updated Paper
                  </button>
                )}
              </div>

              <h3 style={{ marginTop: '1.5rem' }}>Reviewer Comments</h3>
              {paper.review?.reviewerComments && paper.review.reviewerComments.trim() ? (
                <div className="reviewer-comments">
                  <div className="comments-content">
                    {paper.review.reviewerComments}
                  </div>
                  <div className="comments-date">
                    Comments received on: {new Date(paper.review.reviewDate || paper.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="no-comments">
                  <p>No reviewer comments available yet.</p>
                  <p>Comments will appear here once the review process is complete.</p>
                </div>
              )}
            </div>

            {/* Column 3: Upload Updated Manuscript */}
            <div className="status-column">
              <h3>Upload Revised Manuscript</h3>
              {canUploadRevision ? (
                <div className="upload-form">
                  <p className="upload-description">
                    You can upload a revised manuscript based on the reviewer comments.
                  </p>
                  <div className="file-upload-group">
                    <label htmlFor="updatedFile">Select Updated Paper File *</label>
                    <input
                      type="file"
                      id="updatedFile"
                      onChange={handleFileChange}
                      accept=".doc,.docx"
                      disabled={uploading || hasUpdatedManuscript || !canUploadRevision}
                      ref={fileInputRef}
                    />
                    <p className="file-help">
                      Supported formats: .doc, .docx only (Max size: 10MB)
                    </p>
                    {fileError && <div className="file-error">{fileError}</div>}
                  </div>
                  <button
                    className="upload-btn"
                    onClick={handleUploadUpdatedManuscript}
                    disabled={!updatedFile || uploading || hasUpdatedManuscript || !canUploadRevision}
                  >
                    {hasUpdatedManuscript ? 'Revised Manuscript Already Uploaded' : (uploading ? 'Uploading...' : 'Upload Revised Manuscript')}
                  </button>
                  {hasUpdatedManuscript && (
                    <div className="freeze-message">
                      <p>You have already uploaded an revised manuscript. Further uploads are disabled.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="upload-disabled">
                  {recommendation === 'accept' || recommendation === 'accepted' ? (
                    <>
                      <p>✅ Your paper has been accepted without any changes.</p>
                      {/* <p>Revised manuscript upload is disabled.</p> */}
                    </>
                  ) : recommendation.includes('minor') || recommendation.includes('major') || recommendation === 'reject' || recommendation === 'rejected' ? (
                    <>
                      <p>⚠️ You can upload a revised manuscript as per the review recommendation.</p>
                      {hasUpdatedManuscript ? (
                        <p>Revised manuscript already uploaded.</p>
                      ) : (
                        <p>Once the upload window opens, please submit your revised manuscript here.</p>
                      )}
                    </>
                  ) : (
                    <>
                      <p>⚠️ You can only upload a revised manuscript after receiving reviewer comments.</p>
                      <p>Please wait for the review process to complete and check back later.</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Back Button */}
          <div className="back-section">
            <button 
              className="back-btn"
              onClick={() => navigate('/paper-submission')}
            >
              ← Back to Paper Submission
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaperSubmissionStatus; 